import React, { useCallback, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import {
  BrainCircuit,
  Calendar,
  Check,
  CheckCircle,
  Copy,
  Cpu,
  Info,
  Loader2,
  PenTool,
  Printer,
  RefreshCw,
  Settings2,
  Wand2,
  X,
  Zap,
} from 'lucide-react';
import { functions } from '../../../../config/firebase';
import {
  PE_FACTS_DEFAULT_TONALITY,
  PE_FACTS_STANDARD_REASONS,
  PeFactsFlowStep,
  PeFactsGeneratePayload,
  PeFactsGenerateResult,
  PeFactsTonality,
} from '../types';

type Props = {
  showNotification?: (msg: string, type?: 'success' | 'error') => void;
};

const generatePricingResponse = httpsCallable<PeFactsGeneratePayload, PeFactsGenerateResult>(
  functions,
  'generatePricingResponse'
);

/** Entfernt „Meisterschaft“-Formulierungen aus KI-/Fallback-Texten */
const sanitizePeFactsText = (raw: string): string => {
  if (!raw) return '';
  return raw
    .replace(/Meisterschaft unseres Teams/gi, 'fortlaufende Weiterbildung und fachliche Exzellenz unseres Teams')
    .replace(/Meisterschaft/gi, 'fachliche Exzellenz');
};

const PeFactsGenerator: React.FC<Props> = ({ showNotification }) => {
  const [flowStep, setFlowStep] = useState<PeFactsFlowStep>('config');
  const [targetDate, setTargetDate] = useState('');
  const [tonality, setTonality] = useState<PeFactsTonality>(PE_FACTS_DEFAULT_TONALITY);
  const [selectedReasonIds, setSelectedReasonIds] = useState<string[]>([]);
  const [customReasons, setCustomReasons] = useState<string[]>([]);
  const [customDraft, setCustomDraft] = useState('');
  const [resultText, setResultText] = useState('');
  const [refineText, setRefineText] = useState('');
  const [refineBusy, setRefineBusy] = useState(false);
  const [copyFlash, setCopyFlash] = useState(false);

  const notify = useCallback(
    (msg: string, type: 'success' | 'error' = 'success') => {
      if (showNotification) showNotification(msg, type);
      else if (type === 'error') window.alert(msg);
    },
    [showNotification]
  );

  const collectReasons = () => {
    const standard = PE_FACTS_STANDARD_REASONS.filter((r) =>
      selectedReasonIds.includes(r.id)
    ).map((r) => r.value);
    return [...standard, ...customReasons];
  };

  const toggleReason = (id: string) => {
    setSelectedReasonIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleCustomKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const val = customDraft.trim();
    if (!val) return;
    setCustomReasons((prev) => [...prev, val]);
    setCustomDraft('');
  };

  const removeCustom = (index: number) => {
    setCustomReasons((prev) => prev.filter((_, i) => i !== index));
  };

  const generate = async () => {
    if (!targetDate) {
      notify('Bitte gib ein Datum an, ab wann die neuen Preise gelten.', 'error');
      return;
    }
    const reasons = collectReasons();
    if (reasons.length === 0) {
      notify(
        'Bitte wähle mindestens einen Standard-Hebel oder trage einen eigenen Spezial-Hebel ein.',
        'error'
      );
      return;
    }

    setFlowStep('loading');
    try {
      const result = await generatePricingResponse({ targetDate, reasons, tonality });
      setResultText(sanitizePeFactsText(result.data?.text || ''));
      setFlowStep('result');
    } catch (error: any) {
      console.error('PEFacts generate:', error);
      notify(`Fehler bei der KI: ${error?.message || 'Unbekannter Fehler'}`, 'error');
      setFlowStep('config');
    }
  };

  const refine = async () => {
    const instruction = refineText.trim();
    if (!instruction) {
      notify('Bitte gib eine Regie-Anweisung ein, was geändert werden soll.', 'error');
      return;
    }
    setRefineBusy(true);
    try {
      const result = await generatePricingResponse({
        targetDate,
        reasons: collectReasons(),
        tonality,
        refineInstruction: instruction,
      });
      setResultText(sanitizePeFactsText(result.data?.text || ''));
      notify('Text angepasst.', 'success');
    } catch (error: any) {
      console.error('PEFacts refine:', error);
      notify(`Fehler bei der KI: ${error?.message || 'Unbekannter Fehler'}`, 'error');
    } finally {
      setRefineBusy(false);
    }
  };

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(resultText);
      setCopyFlash(true);
      notify('Text kopiert.', 'success');
      setTimeout(() => setCopyFlash(false), 2000);
    } catch {
      notify('Kopieren fehlgeschlagen.', 'error');
    }
  };

  const printText = () => {
    const w = window.open('', '_blank', 'noopener,noreferrer,width=800,height=900');
    if (!w) {
      notify('Popup blockiert – bitte Popups erlauben.', 'error');
      return;
    }
    const safe = resultText
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\n/g, '<br/>');
    w.document.write(`<!DOCTYPE html><html lang="de"><head><meta charset="UTF-8"/><title>Preisanpassungs-Aufsteller</title>
<style>
  body{font-family:Georgia,'Times New Roman',serif;color:#111;background:#fff;margin:0;padding:18mm 16mm;line-height:1.55;font-size:14px;}
  h1{font-size:20px;margin:0 0 12px;letter-spacing:0.02em;}
  .meta{font-size:11px;color:#64748b;text-transform:uppercase;letter-spacing:0.08em;margin-bottom:18px;}
  .body{white-space:normal;}
  @media print{body{padding:12mm;}}
</style></head><body>
<h1>Preisanpassung</h1>
<div class="meta">Aufsteller · salonsicher</div>
<div class="body">${safe}</div>
<script>window.onload=function(){window.print();}</script>
</body></html>`);
    w.document.close();
  };

  const resetFlow = () => {
    setFlowStep('config');
    setRefineText('');
  };

  return (
    <div className="bg-[#0a0f1c] p-6 md:p-8 flex flex-col h-full overflow-y-auto" id="pe-facts-generator">
      {flowStep === 'config' && (
        <div className="space-y-8 animate-fadeIn mt-2">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-xl font-bold text-white flex items-center gap-2">
              <Cpu className="w-6 h-6 text-blue-400" /> Fakten konfigurieren
            </h3>
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-bold text-slate-300 mb-2">Tonalität</label>
              <div className="inline-flex p-1 bg-slate-950 border border-slate-800 rounded-lg mb-6">
                <button
                  type="button"
                  onClick={() => setTonality('sie')}
                  className={`px-3 py-2 rounded-md text-xs transition-colors ${
                    tonality === 'sie'
                      ? 'bg-slate-800 text-amber-200 border border-amber-400/30 font-semibold shadow-sm'
                      : 'text-slate-400 hover:text-slate-200 border border-transparent'
                  }`}
                >
                  Sie (Formell)
                </button>
                <button
                  type="button"
                  onClick={() => setTonality('du')}
                  className={`px-3 py-2 rounded-md text-xs transition-colors ${
                    tonality === 'du'
                      ? 'bg-slate-800 text-amber-200 border border-amber-400/30 font-semibold shadow-sm'
                      : 'text-slate-400 hover:text-slate-200 border border-transparent'
                  }`}
                >
                  Du (Persönlich)
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-slate-300 mb-2 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#E6C299]" /> Ab wann gelten die neuen Preise?
              </label>
              <input
                type="date"
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 rounded-xl p-4 text-sm text-white focus:border-blue-500 mb-2 outline-none"
              />
              <p className="text-[10px] text-slate-500 mb-6">
                Wird prominent im Aufsteller kommuniziert.
              </p>
            </div>

            <div className="pt-2 border-t border-slate-800">
              <label className="block text-sm font-bold text-slate-300 mt-4 mb-2 flex items-center gap-2">
                <Settings2 className="w-4 h-4 text-blue-400" /> Standard-Hebel (Optional)
              </label>
              <p className="text-[10px] text-[#E6C299] mb-4">
                Wähle klassische Gründe für die Anpassung. Keine Ausreden.
              </p>

              <div className="space-y-3 mb-6">
                {PE_FACTS_STANDARD_REASONS.map((reason) => {
                  const checked = selectedReasonIds.includes(reason.id);
                  return (
                    <label
                      key={reason.id}
                      className="relative flex items-center p-4 border border-slate-700 rounded-xl bg-slate-950 hover:bg-slate-800/50 cursor-pointer transition-all"
                    >
                      <input
                        type="checkbox"
                        className="peer sr-only"
                        checked={checked}
                        onChange={() => toggleReason(reason.id)}
                      />
                      <div
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center mr-4 shrink-0 transition-colors ${
                          checked
                            ? 'bg-[#E6C299] border-[#E6C299]'
                            : 'border-slate-500 bg-transparent'
                        }`}
                      >
                        <Check
                          className={`w-3 h-3 text-black transition-opacity ${
                            checked ? 'opacity-100' : 'opacity-0'
                          }`}
                        />
                      </div>
                      <span
                        className={`text-sm font-bold transition-colors ${
                          checked ? 'text-white' : 'text-slate-300'
                        }`}
                      >
                        {reason.label}
                      </span>
                    </label>
                  );
                })}
              </div>

              <label className="block text-sm font-bold text-slate-300 mb-2 flex items-center gap-2">
                <Zap className="w-4 h-4 text-amber-400" /> Deine eigenen Spezial-Hebel
              </label>
              <p className="text-[10px] text-slate-500 mb-2">
                Tippe einen Grund ein und drücke <strong className="text-white">ENTER</strong>, um
                ihn hinzuzufügen.
              </p>
              <div className="relative">
                <input
                  type="text"
                  value={customDraft}
                  onChange={(e) => setCustomDraft(e.target.value)}
                  onKeyDown={handleCustomKey}
                  placeholder="z.B. Längere und entspanntere Terminfenster..."
                  className="w-full bg-slate-950 border border-slate-700 rounded-xl p-4 pr-20 text-sm text-white focus:border-amber-400 outline-none"
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-xs font-mono border border-slate-700 px-2 py-1 rounded bg-slate-900">
                  ↵ Enter
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-3 min-h-[24px]">
                {customReasons.map((reason, i) => (
                  <span
                    key={`${reason}-${i}`}
                    className="bg-amber-500/10 text-amber-400 border border-amber-500/30 px-3 py-1.5 rounded-lg text-xs flex items-center gap-2 shadow-sm"
                  >
                    {reason}
                    <button
                      type="button"
                      onClick={() => removeCustom(i)}
                      className="hover:text-red-400 transition-colors ml-1 focus:outline-none"
                      aria-label="Hebel entfernen"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            <div className="bg-[#E6C299]/10 border border-[#E6C299]/30 p-4 rounded-xl mt-6">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-[#E6C299] shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-bold text-[#E6C299]">KI-Direktive aktiv</h4>
                  <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                    Die KI ist vom Backend fest darauf programmiert, kurz, wertschätzend und{' '}
                    <strong>ohne jede Entschuldigung</strong> zu texten.
                  </p>
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={() => void generate()}
              className="w-full py-4 rounded-xl border border-blue-500 bg-blue-600 hover:bg-blue-500 text-white font-black text-xs md:text-sm uppercase tracking-widest transition-all shadow-[0_0_20px_rgba(59,130,246,0.2)] flex items-center justify-center gap-2 mt-4"
            >
              <Wand2 className="w-5 h-5" /> Aufsteller-Text generieren
            </button>
          </div>
        </div>
      )}

      {flowStep === 'loading' && (
        <div className="flex flex-col items-center justify-center flex-1 py-20">
          <div className="relative w-16 h-16 mb-6">
            <div className="absolute inset-0 border-4 border-slate-800 rounded-full" />
            <div className="absolute inset-0 border-4 border-[#E6C299] rounded-full border-t-transparent animate-spin" />
            <BrainCircuit className="absolute inset-0 m-auto w-6 h-6 text-[#E6C299] animate-pulse" />
          </div>
          <h3 className="text-white font-bold text-lg mb-2">KI formuliert strategischen Text...</h3>
          <p className="text-slate-500 font-mono text-xs uppercase tracking-widest text-center">
            Lösche alle Rechtfertigungen...
          </p>
        </div>
      )}

      {flowStep === 'result' && (
        <div className="flex flex-col space-y-8 pb-10">
          <div className="flex items-center justify-between border-b border-slate-800 pb-4">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-[#E6C299]" /> Dein fertiger Aufsteller
            </h3>
            <button
              type="button"
              onClick={resetFlow}
              className="text-[10px] text-slate-500 hover:text-white uppercase tracking-widest font-bold flex items-center gap-1 transition-colors bg-slate-800 px-3 py-1.5 rounded-lg border border-slate-700"
            >
              <RefreshCw className="w-3 h-3" /> Neu erstellen
            </button>
          </div>

          <div className="space-y-6">
            <div className="flex flex-col gap-2">
              <textarea
                rows={14}
                readOnly
                value={resultText}
                className="w-full bg-slate-950 border border-slate-700 rounded-xl p-5 text-sm text-slate-200 leading-relaxed"
              />
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={printText}
                  className="bg-slate-800 hover:bg-slate-700 px-4 py-2 rounded-lg text-white text-xs font-bold flex items-center gap-2 transition-colors border border-slate-600 shadow-sm"
                >
                  <Printer className="w-4 h-4" /> Drucken
                </button>
                <button
                  type="button"
                  onClick={() => void copyText()}
                  className="bg-slate-800 hover:bg-slate-700 px-4 py-2 rounded-lg text-white text-xs font-bold flex items-center gap-2 transition-colors border border-slate-600 shadow-sm"
                >
                  {copyFlash ? (
                    <>
                      <Check className="w-4 h-4 text-green-400" /> Kopiert!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" /> Text kopieren
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="mt-2 flex flex-col md:flex-row items-center gap-3 bg-slate-800/20 p-3 rounded-xl border border-slate-700/50">
              <div className="relative w-full flex-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <PenTool className="w-4 h-4 text-slate-500" />
                </div>
                <input
                  type="text"
                  value={refineText}
                  onChange={(e) => setRefineText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void refine();
                  }}
                  placeholder="Regie-Anweisung (z.B. Mach es noch knapper...)"
                  className="w-full bg-slate-950 border border-slate-700 rounded-xl pl-10 pr-4 py-3 text-sm text-white focus:border-[#E6C299] outline-none"
                />
              </div>
              <button
                type="button"
                disabled={refineBusy}
                onClick={() => void refine()}
                className="w-full md:w-auto px-5 py-3 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-xl text-white font-bold text-sm transition-colors whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {refineBusy ? (
                  <Loader2 className="w-4 h-4 animate-spin text-[#E6C299]" />
                ) : (
                  <RefreshCw className="w-4 h-4 text-[#E6C299]" />
                )}
                Anpassen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PeFactsGenerator;
